<template>
  <q-dialog
    ref="dialogRef"
    seamless
    :no-backdrop-dismiss="isUrgent"
    :no-esc-dismiss="isUrgent"
    :class="[
      'message-toast-dialog tw-z-[1000000000]',
      { 'message-toast-dialog--stacked': !isUrgent && isStacked },
    ]"
    transition-show="slide-left"
    transition-hide="slide-right"
    @hide="onDialogHide"
  >
    <div
      :class="[
        'tw-w-[360px] tw-max-w-[calc(100vw-32px)] tw-bg-[#293f45] !tw-rounded-lg tw-p-4 tw-pb-5 tw-shadow-md tw-relative',
        { 'tw-border-l-4 tw-border-red-400': isUrgent },
      ]"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <q-linear-progress
        v-if="!isUrgent"
        :value="progress"
        color="primary"
        track-color="transparent"
        size="3px"
        instant-feedback
        class="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-rounded-b-lg"
      />
      <div class="tw-flex tw-items-start tw-gap-3">
        <component
          :is="isUrgent ? MessageSquareWarning : MessageSquareDot"
          :size="18"
          class="tw-flex-shrink-0 tw-mt-0.5"
          :class="isUrgent ? 'tw-text-red-400' : 'tw-text-emerald-400'"
        />
        <div class="tw-flex-1 tw-min-w-0">
          <div
            class="tw-text-[0.8125rem] tw-font-semibold tw-text-white tw-leading-snug tw-truncate"
          >
            {{ senderName }}
          </div>
          <div
            class="tw-text-[0.75rem] tw-text-slate-300 tw-mt-0.5 tw-leading-snug tw-line-clamp-3"
          >
            {{ truncatedContent }}
          </div>
        </div>
        <div
          class="tw-flex tw-items-center tw-gap-0.5 tw--mt-[3px] tw--mr-[3px] tw-flex-shrink-0"
        >
          <button
            class="tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-slate-500 tw-rounded tw-p-1.5 tw-transition-colors hover:tw-text-slate-300"
            @click="onSettings"
          >
            <Settings :size="16" />
            <q-tooltip
              class="tw-text-[0.6875rem]"
              anchor="top middle"
              self="bottom middle"
            >
              {{ $t('notificationSettings.messagingNotifications') }}
            </q-tooltip>
          </button>
          <button
            class="tw-relative tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-slate-500 tw-rounded tw-p-1.5 tw-transition-colors hover:tw-text-slate-300"
            @click="onClose"
          >
            <ChevronRight v-if="hasMore" :size="16" />
            <X v-else :size="16" />
            <span
              v-if="hasMore"
              class="tw-absolute tw--top-1.5 tw--right-1.5 tw-bg-emerald-500 tw-text-white tw-text-[0.625rem] tw-font-bold tw-rounded-full tw-min-w-[18px] tw-h-[18px] tw-flex tw-items-center tw-justify-center tw-leading-none tw-px-1"
            >
              {{ queueSize }}
            </span>
            <q-tooltip
              v-if="hasMore"
              class="tw-text-[0.6875rem]"
              anchor="top middle"
              self="bottom middle"
            >
              {{ queueSize }} {{ $t('more') }}
            </q-tooltip>
          </button>
        </div>
      </div>

      <div
        class="tw-flex tw-items-center tw-justify-end tw-mt-3 tw-pt-2.5 tw-border-t tw-border-white/[0.07]"
      >
        <button
          v-if="crossCenterId"
          class="tw-flex tw-items-center tw-gap-[5px] tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-amber-400 tw-text-[0.75rem] tw-font-semibold tw-p-0 tw-transition-colors hover:tw-text-amber-300"
          @click="onReply"
        >
          <ArrowLeftRight :size="13" />
          {{ $t('switchCenter') }}
        </button>
        <button
          v-else
          class="tw-flex tw-items-center tw-gap-[5px] tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-emerald-400 tw-text-[0.75rem] tw-font-semibold tw-p-0 tw-transition-colors hover:tw-text-emerald-300"
          @click="onReply"
        >
          <MessageSquareShare :size="13" />
          {{ $t('reply') }}
        </button>
      </div>
    </div>
  </q-dialog>
</template>

<script lang="ts" setup>
import { useDialogPluginComponent } from 'quasar'
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import {
  ArrowLeftRight,
  ChevronRight,
  MessageSquareDot,
  MessageSquareShare,
  MessageSquareWarning,
  Settings,
  X,
} from 'lucide-vue-next'
import { useMessagingServiceStore } from 'src/stores/messaging.service.store'

const props = defineProps<{
  senderName: string
  content: string
  isUrgent: boolean
  conversationId: string
  crossCenterId?: string
}>()

defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent()

const messagingServiceStore = useMessagingServiceStore()
const hasMore = computed(() =>
  props.isUrgent
    ? messagingServiceStore.urgentToastQueueSize > 0
    : messagingServiceStore.regularToastQueueSize > 0,
)
const queueSize = computed(() =>
  props.isUrgent
    ? messagingServiceStore.urgentToastQueueSize
    : messagingServiceStore.regularToastQueueSize,
)
const isStacked = computed(() => messagingServiceStore.isUrgentToastActive)

const DURATION = 6000
const progress = ref(1)
let rafId: number | undefined
let remaining = DURATION
let lastTick = 0

function startTicking() {
  lastTick = Date.now()
  rafId = requestAnimationFrame(tick)
}

function tick() {
  const now = Date.now()
  remaining -= now - lastTick
  lastTick = now
  if (remaining <= 0) {
    progress.value = 0
    onDialogOK({ action: 'timeout' })
    return
  }
  progress.value = remaining / DURATION
  rafId = requestAnimationFrame(tick)
}

function stopTicking() {
  if (rafId !== undefined) {
    cancelAnimationFrame(rafId)
    rafId = undefined
  }
}

const truncatedContent =
  props.content.length > 120
    ? props.content.slice(0, 120) + '\u2026'
    : props.content

onMounted(() => {
  if (!props.isUrgent) {
    startTicking()
  }
})

onBeforeUnmount(() => {
  stopTicking()
})

function onReply() {
  stopTicking()
  onDialogOK({ action: 'reply' })
}

function onSettings() {
  stopTicking()
  onDialogOK({ action: 'settings' })
}

function onClose() {
  stopTicking()
  onDialogOK({ action: 'dismiss' })
}

function onMouseEnter() {
  stopTicking()
}

function onMouseLeave() {
  if (!props.isUrgent && remaining > 0) {
    startTicking()
  }
}
</script>

<style lang="sass">
.message-toast-dialog
  .q-dialog__inner.q-transition--slide-left-enter-active,
  .q-dialog__inner.q-transition--slide-right-leave-active
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease !important
  .q-dialog__inner.q-transition--slide-left-enter-from,
  .q-dialog__inner.q-transition--slide-right-leave-to
    transform: translate3d(calc(100% + 24px), 0, 0) !important
    opacity: 0
  .q-dialog__inner
    position: fixed !important
    top: auto !important
    left: auto !important
    right: 24px !important
    bottom: 24px !important
    padding: 0 !important
    max-height: none !important
    justify-content: flex-end !important
    align-items: flex-end !important
    outline: none !important

.message-toast-dialog--stacked
  .q-dialog__inner
    bottom: 148px !important
</style>
